import { DeviceHubIcon, PestControlIcon, StackedLineChartIcon } from '@protector/icons';
import type { TabsProps } from 'antd';
import { useMediaQuery } from 'beautiful-react-hooks';
import Header from 'components/header';
import { MEDIA_QUERIES } from 'config/style/breakpoints';
import useSegmentTracking from 'core/utils/segment/useSegmentTracking';
import { useRegionalOverviewNavigate } from '@features/regional-overview/utils';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { DAILY_COUNT, FLIGHT_CURVE, NETWORK_MANAGEMENT } from '../domain';
import DailyCounts from './tabs/daily-counts/daily-counts.component';
import FlightCurve from './tabs/flight-curve/flight-curve.component';
import NetworkManagement from './tabs/network-management/network-management.component';
import { MobileHeaderWrapper, StyledTrapManagementTabs } from './trap-management-tabs.style';

type TrapManagementTabKey = typeof DAILY_COUNT | typeof FLIGHT_CURVE | typeof NETWORK_MANAGEMENT;

const TAB_ANALYTICS_EVENTS: Record<TrapManagementTabKey, string> = {
  [DAILY_COUNT]: 'Digital Traps - Pest Pressure Analysis - Daily Counts',
  [FLIGHT_CURVE]: 'Digital Traps - Pest Pressure Analysis - Flight Curve',
  [NETWORK_MANAGEMENT]: 'Digital Traps - Pest Pressure Analysis - Network Management'
};

const TrapManagementTabs = (): JSX.Element => {
  const navigate = useNavigate();
  const navigateTo = useRegionalOverviewNavigate();
  const { model, selectedTab } = useParams();

  const [t] = useTranslation();
  const isMobile = useMediaQuery(MEDIA_QUERIES.MOBILE);
  const segmentTracking = useSegmentTracking();

  useEffect(() => {
    const activeTab = document.querySelector<HTMLElement>('.ant-tabs-nav-wrap .ant-tabs-tab-active');
    activeTab?.scrollIntoView?.({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
  }, [selectedTab]);

  const handleTabChange = (key: string) => {
    if (!model) {
      return;
    }

    const analyticsEvent = TAB_ANALYTICS_EVENTS[key as TrapManagementTabKey];
    if (analyticsEvent) {
      segmentTracking.track(analyticsEvent);
    }

    navigateTo(model).trapManagement(key, { replace: true });
  };

  const items: TabsProps['items'] = [
    {
      key: DAILY_COUNT,
      label: t('pages.regional_overview.digital_traps.daily_counts.tab_label'),
      icon: <PestControlIcon fontSize={24} />,
      children: <DailyCounts />
    },
    {
      key: FLIGHT_CURVE,
      label: t('pages.regional_overview.digital_traps.flight_curve.tab_label'),
      icon: <StackedLineChartIcon fontSize={24} />,
      children: <FlightCurve />,
      disabled: false
    },
    {
      key: NETWORK_MANAGEMENT,
      label: t('pages.regional_overview.digital_traps.network_management.tab_label'),
      icon: <DeviceHubIcon fontSize={24} />,
      children: <NetworkManagement />
    }
  ];

  const pageHeader = <Header onClickBack={() => navigate(-1)} title={t('pages.regional_overview.digital_traps.trap_management')} />;

  const tabBarExtraContent = isMobile ? undefined : { left: pageHeader };

  return (
    <>
      {isMobile && <MobileHeaderWrapper>{pageHeader}</MobileHeaderWrapper>}
      <StyledTrapManagementTabs items={items} tabBarExtraContent={tabBarExtraContent} activeKey={selectedTab} onChange={handleTabChange} />
    </>
  );
};

export default TrapManagementTabs;
